import React, { useState } from 'react';
import { X, Smartphone, User, CheckCircle, AlertCircle, ChevronRight, ArrowLeft, Wallet } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { formatCurrency } from '../../utils/helpers';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { BACKEND_URL } from '../../config/backend';

const NETWORKS = [
  { id: 'mtn', label: 'MTN MoMo', color: 'from-yellow-400 to-yellow-500', textColor: 'text-yellow-900', borderColor: 'border-yellow-400', bg: 'bg-yellow-50', emoji: '🟡' },
  { id: 'moov', label: 'Moov Money', color: 'from-blue-500 to-blue-600', textColor: 'text-blue-900', borderColor: 'border-blue-400', bg: 'bg-blue-50', emoji: '🔵' },
  { id: 'other', label: 'Autre', color: 'from-gray-400 to-gray-500', textColor: 'text-gray-900', borderColor: 'border-gray-400', bg: 'bg-gray-50', emoji: '💳' },
];

const WithdrawalRequestModal = ({ plan, user, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    network: '',
    phoneNumber: '',
    recipientName: '',
  });

  const selectedNetwork = NETWORKS.find(n => n.id === formData.network);
  const amount = plan?.current_balance || 0;

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleNext = () => {
    if (!formData.network) { setError('Veuillez sélectionner un réseau Mobile Money'); return; }
    if (!formData.phoneNumber.trim()) { setError('Veuillez entrer un numéro de téléphone'); return; }
    if (!/^[\d\s+\-()]{8,}$/.test(formData.phoneNumber)) { setError('Numéro de téléphone invalide'); return; }
    if (!formData.recipientName.trim()) { setError('Veuillez entrer le nom du bénéficiaire'); return; }
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: withdrawalData, error: withdrawalError } = await supabase
        .from('withdrawal_requests')
        .insert([{
          user_id: plan.user_id,
          savings_plan_id: plan.id,
          amount,
          phone_number: formData.phoneNumber.trim(),
          recipient_name: formData.recipientName.trim(),
          status: 'pending',
        }])
        .select()
        .single();

      if (withdrawalError) throw new Error('Erreur lors de la création de la demande');

      await supabase
        .from('savings_plans')
        .update({ status: 'withdrawal_pending', updated_at: new Date().toISOString() })
        .eq('id', plan.id);

      const clientName = user
        ? `${user.first_name || user.user_metadata?.first_name || ''} ${user.last_name || user.user_metadata?.last_name || ''}`.trim()
        : 'Client';

      try {
        await fetchWithAuth(`${BACKEND_URL}/api/notify-admin-withdrawal`, {
          method: 'POST',
          body: JSON.stringify({
            withdrawalId: withdrawalData.id,
            clientName,
            amount,
            userId: plan.user_id,
            planId: plan.id,
          }),
        });
      } catch (_) { /* ne bloque pas */ }

      setStep(3);
      setTimeout(() => {
        if (onSuccess) onSuccess('Demande de retrait envoyée avec succès !');
        onClose();
      }, 2500);
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3 sm:p-4">
      <div className="bg-white w-full sm:max-w-sm md:max-w-md lg:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden max-h-[90vh] sm:max-h-[85vh] flex flex-col">

        {/* ── Header ── */}
        <div className="relative bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 px-5 pt-4 pb-6 sm:px-6 sm:pt-5 sm:pb-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
          >
            <X size={20} className="text-white" />
          </button>

          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Wallet size={20} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-green-100 text-[10px] sm:text-xs font-medium uppercase tracking-widest truncate">Retrait</p>
              <h2 className="text-white text-sm sm:text-base font-bold truncate">{plan?.plan_name || 'Mon Plan'}</h2>
            </div>
          </div>

          <div className="text-center">
            <p className="text-green-100 text-xs mb-1">Montant disponible</p>
            <p className="text-white text-3xl sm:text-4xl font-extrabold">{formatCurrency(amount)}</p>
          </div>

          {/* Step indicator */}
          {step < 3 && (
            <div className="flex items-center justify-center gap-1.5 mt-3 sm:mt-4">
              {[1, 2].map(s => (
                <React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full text-xs font-bold transition-all duration-300 ${
                    step >= s ? 'bg-white text-emerald-600 shadow-lg' : 'bg-white/30 text-white'
                  }`}>
                    {step > s ? <CheckCircle size={12} /> : s}
                  </div>
                  {s < 2 && <div className={`h-0.5 w-6 sm:w-8 rounded-full transition-all duration-500 ${step > 1 ? 'bg-white' : 'bg-white/30'}`} />}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div className="-mt-3 sm:-mt-4 bg-white rounded-t-2xl px-4 sm:px-5 pt-4 sm:pt-5 pb-5 sm:pb-6 overflow-y-auto flex-1">

          {/* Étape 1 : Formulaire */}
          {step === 1 && (
            <div className="space-y-3 sm:space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Réseau Mobile Money</p>
                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  {NETWORKS.map(n => (
                    <button
                      key={n.id}
                      onClick={() => { setFormData(prev => ({ ...prev, network: n.id })); setError(''); }}
                      className={`flex flex-col items-center gap-1 py-2 px-1.5 sm:py-3 sm:px-2 rounded-xl sm:rounded-2xl border-2 font-semibold text-xs transition-all duration-200 ${
                        formData.network === n.id
                          ? `${n.bg} ${n.borderColor} ${n.textColor} shadow-md`
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg sm:text-xl">{n.emoji}</span>
                      <span className="hidden sm:block">{n.label}</span>
                      <span className="sm:hidden text-xs">{n.label.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Numéro {selectedNetwork ? selectedNetwork.label : 'Mobile Money'}
                </label>
                <div className="relative">
                  <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
                    <Smartphone size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    placeholder="+229 XX XX XX XX"
                    className="w-full pl-9 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Nom sur le compte
                </label>
                <div className="relative">
                  <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
                    <User size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="recipientName"
                    value={formData.recipientName}
                    onChange={handleChange}
                    placeholder="Nom complet du bénéficiaire"
                    className="w-full pl-9 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl sm:rounded-2xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-red-700">{error}</p>
                </div>
              )}

              <button
                onClick={handleNext}
                className="w-full py-3 sm:py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                Continuer
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          {/* Étape 2 : Confirmation */}
          {step === 2 && (
            <div className="space-y-3 sm:space-y-4">
              <div className="space-y-2 sm:space-y-2.5">
                {[
                  { label: 'Montant à recevoir', value: formatCurrency(amount), highlight: true },
                  { label: 'Réseau', value: selectedNetwork?.label || '—' },
                  { label: 'Numéro Mobile Money', value: formData.phoneNumber },
                  { label: 'Bénéficiaire', value: formData.recipientName },
                ].map(({ label, value, highlight }) => (
                  <div key={label} className={`flex justify-between items-center px-3 sm:px-4 py-2 sm:py-3 rounded-xl sm:rounded-2xl ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'}`}>
                    <span className="text-xs sm:text-sm text-gray-500">{label}</span>
                    <span className={`font-bold text-right ${highlight ? 'text-emerald-600 text-sm sm:text-lg' : 'text-gray-900 text-sm'}`}>{value}</span>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3">
                <p className="text-xs text-amber-800 leading-relaxed">
                  ⏳ Votre demande sera traitée par un administrateur. Vous serez notifié dès que le transfert est effectué.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex gap-2 sm:gap-3 pt-1">
                <button
                  onClick={() => setStep(1)}
                  disabled={loading}
                  className="flex items-center justify-center gap-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-sm rounded-xl sm:rounded-2xl transition-all duration-200 disabled:opacity-50"
                >
                  <ArrowLeft size={16} />
                  <span className="hidden sm:inline">Retour</span>
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-2.5 sm:py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span className="hidden sm:inline">Envoi...</span>
                    </span>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Confirmer le retrait</span>
                      <span className="sm:hidden">Confirmer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Étape 3 : Succès */}
          {step === 3 && (
            <div className="flex flex-col items-center text-center py-2 sm:py-4 space-y-3">
              <div className="w-16 sm:w-20 h-16 sm:h-20 bg-emerald-100 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle size={36} className="text-emerald-500 sm:w-11 sm:h-11" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Demande envoyée !</h3>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed">L'administrateur va traiter votre retrait de <strong className="text-emerald-600">{formatCurrency(amount)}</strong>.<br />Vous serez notifié.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequestModal;
